// src/hooks/useWHOGrowthCharts.js
//
// Hook para construir las tablas de crecimiento OMS (Opción A):
// - Lee el histórico de consultas del paciente (peso/talla/IMC/perímetro cefálico)
// - Calcula edad (en meses) en cada consulta a partir de fecha_nacimiento
// - Calcula z-score y percentil con la fórmula LMS oficial de la OMS
// - Genera los datasets de curvas de referencia (P3/P15/P50/P85/P97) + puntos del paciente
//
// Datos OMS estáticos en src/data/who/<indicador>_<sexo>.json (LMS oficiales).

import { useEffect, useMemo, useState } from 'react';
import axiosInstance from '../axiosInstance';

// Importación estática de los datasets OMS (se empaquetan en el build)
import weightBoys from '../data/who/weight_boys.json';
import weightGirls from '../data/who/weight_girls.json';
import heightBoys from '../data/who/height_boys.json';
import heightGirls from '../data/who/height_girls.json';
import bmiBoys from '../data/who/bmi_boys.json';
import bmiGirls from '../data/who/bmi_girls.json';
import headBoys from '../data/who/headcirc_boys.json';
import headGirls from '../data/who/headcirc_girls.json';

const WHO = {
  weight: { boys: weightBoys, girls: weightGirls },
  height: { boys: heightBoys, girls: heightGirls },
  bmi: { boys: bmiBoys, girls: bmiGirls },
  headcirc: { boys: headBoys, girls: headGirls },
};

// Indicadores disponibles y de qué campo de la consulta salen
export const INDICATORS = [
  { key: 'weight', label: 'Peso para la edad', unit: 'kg', field: 'peso' },
  { key: 'height', label: 'Talla / Longitud para la edad', unit: 'cm', field: 'altura' },
  { key: 'bmi', label: 'IMC para la edad', unit: 'kg/m²', field: 'imc' },
  { key: 'headcirc', label: 'Perímetro cefálico para la edad', unit: 'cm', field: 'perimetro_cefalico' },
];

// Z-scores de las curvas de referencia a dibujar y su etiqueta de percentil
const REFERENCE_LINES = [
  { z: -2.054, label: 'P3' },   // percentil 3
  { z: -1.036, label: 'P15' },  // percentil 15
  { z: 0, label: 'P50' },       // mediana
  { z: 1.036, label: 'P85' },   // percentil 85
  { z: 1.881, label: 'P97' },   // percentil 97
];

const DAYS_PER_MONTH = 30.4375;

// ---- Utilidades numéricas ----

// Edad en meses entre dos fechas (Date)
export function ageInMonths(birthDate, atDate) {
  const b = new Date(birthDate);
  const a = new Date(atDate);
  if (isNaN(b) || isNaN(a)) return null;
  const days = (a - b) / (1000 * 60 * 60 * 24);
  return days / DAYS_PER_MONTH;
}

// Función de distribución normal acumulada (para z -> percentil)
function normalCdf(z) {
  // Aproximación de Abramowitz & Stegun (error < 7.5e-8)
  const t = 1 / (1 + 0.2316419 * Math.abs(z));
  const d = 0.3989422804014327 * Math.exp(-z * z / 2);
  let p =
    d *
    t *
    (0.3193815 + t * (-0.3565638 + t * (1.781478 + t * (-1.821256 + t * 1.330274))));
  if (z > 0) p = 1 - p;
  return 1 - p;
}

// Interpola los parámetros LMS para una edad dada (meses)
function interpolateLMS(data, month) {
  if (!data || !data.length) return null;
  if (month <= data[0].m) return data[0];
  if (month >= data[data.length - 1].m) return data[data.length - 1];
  for (let i = 0; i < data.length - 1; i++) {
    const a = data[i];
    const b = data[i + 1];
    if (month >= a.m && month <= b.m) {
      const f = (month - a.m) / (b.m - a.m);
      return {
        m: month,
        L: a.L + (b.L - a.L) * f,
        M: a.M + (b.M - a.M) * f,
        S: a.S + (b.S - a.S) * f,
      };
    }
  }
  return data[data.length - 1];
}

// z-score a partir de un valor medido (fórmula LMS de la OMS)
export function valueToZ(value, L, M, S) {
  if (S === 0 || M === 0) return null;
  if (Math.abs(L) < 1e-7) return Math.log(value / M) / S;
  return (Math.pow(value / M, L) - 1) / (L * S);
}

// valor medido a partir de un z-score (para dibujar las curvas de referencia)
export function zToValue(z, L, M, S) {
  if (Math.abs(L) < 1e-7) return M * Math.exp(S * z);
  return M * Math.pow(1 + L * S * z, 1 / L);
}

// z-score a percentil (0-100)
export function zToPercentile(z) {
  return normalCdf(z) * 100;
}

// Parsea un valor de consulta que puede venir como "12.5 Kg", "98 cm", "16.2", etc.
function parseMeasurement(raw) {
  if (raw === null || raw === undefined) return null;
  const m = String(raw).replace(',', '.').match(/-?\d+(\.\d+)?/);
  return m ? parseFloat(m[0]) : null;
}

// ---- Hook principal ----

export function useWHOGrowthCharts(pacienteId) {
  const [paciente, setPaciente] = useState(null);
  const [consultas, setConsultas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let abort = false;
    if (!pacienteId) {
      setLoading(false);
      return;
    }
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const [pacRes, consRes] = await Promise.all([
          axiosInstance.get(`/pacientes/${pacienteId}/`),
          axiosInstance.get(`/consultas/`, { params: { paciente: pacienteId } }),
        ]);
        if (abort) return;
        setPaciente(pacRes.data);
        // El endpoint puede devolver array directo o {results: []}
        const list = Array.isArray(consRes.data)
          ? consRes.data
          : consRes.data?.results || [];
        setConsultas(list);
      } catch (e) {
        if (!abort) setError('No se pudieron cargar los datos del paciente.');
      } finally {
        if (!abort) setLoading(false);
      }
    })();
    return () => {
      abort = true;
    };
  }, [pacienteId]);

  const sexKey = useMemo(() => {
    const s = (paciente?.sexo || '').toString().toLowerCase();
    return s.startsWith('masc') || s === 'm' ? 'boys' : 'girls';
  }, [paciente]);

  // ¿Es paciente pediátrico? (< 19 años para curvas, y mostramos botón si <= 19)
  const edadAnios = useMemo(() => {
    if (!paciente?.fecha_nacimiento) return null;
    const m = ageInMonths(paciente.fecha_nacimiento, new Date());
    return m == null ? null : m / 12;
  }, [paciente]);

  const isPediatric = edadAnios != null && edadAnios <= 19;

  // Puntos reales del paciente por indicador (con edad, valor, z y percentil)
  const patientPoints = useMemo(() => {
    if (!paciente?.fecha_nacimiento) return {};
    const result = {};
    INDICATORS.forEach((ind) => {
      const data = WHO[ind.key]?.[sexKey]?.data;
      const pts = [];
      consultas.forEach((c) => {
        const fecha = c.fecha_hora_consulta;
        if (!fecha) return;
        const value = parseMeasurement(c[ind.field]);
        if (value == null || value <= 0) return;
        const month = ageInMonths(paciente.fecha_nacimiento, fecha);
        if (month == null || month < 0) return;
        // Solo dentro del rango de datos OMS de ese indicador
        const maxM = data[data.length - 1].m;
        if (month > maxM + 1) return;
        const lms = interpolateLMS(data, month);
        const z = valueToZ(value, lms.L, lms.M, lms.S);
        pts.push({
          x: +month.toFixed(2),
          y: value,
          fecha,
          z: z == null ? null : +z.toFixed(2),
          percentile: z == null ? null : +zToPercentile(z).toFixed(1),
        });
      });
      pts.sort((a, b) => a.x - b.x);
      result[ind.key] = pts;
    });
    return result;
  }, [consultas, paciente, sexKey]);

  // Construye la configuración de datos para Chart.js de un indicador
  const buildChartData = useMemo(() => {
    return (indicatorKey) => {
      const data = WHO[indicatorKey]?.[sexKey]?.data;
      if (!data) return null;

      // Rango de edad a mostrar: limita al máximo del indicador y a la edad del niño + margen
      const maxData = data[data.length - 1].m;
      const childMax = edadAnios != null ? Math.min(maxData, edadAnios * 12 + 6) : maxData;
      const upper = Math.max(12, Math.min(maxData, childMax)); // al menos 12 meses

      const refData = data.filter((d) => d.m <= upper + 0.01);

      const refColors = {
        P3: 'rgba(239,68,68,0.55)',
        P15: 'rgba(245,158,11,0.55)',
        P50: 'rgba(34,197,94,0.9)',
        P85: 'rgba(245,158,11,0.55)',
        P97: 'rgba(239,68,68,0.55)',
      };

      const refDatasets = REFERENCE_LINES.map((line) => ({
        label: line.label,
        data: refData.map((d) => ({
          x: d.m,
          y: +zToValue(line.z, d.L, d.M, d.S).toFixed(2),
        })),
        borderColor: refColors[line.label],
        borderWidth: line.label === 'P50' ? 2 : 1,
        borderDash: line.label === 'P50' ? [] : [5, 4],
        pointRadius: 0,
        fill: false,
        tension: 0.3,
        order: 2,
      }));

      const patientDataset = {
        label: 'Paciente',
        data: (patientPoints[indicatorKey] || []).map((p) => ({ x: p.x, y: p.y })),
        borderColor: '#2563eb',
        backgroundColor: '#2563eb',
        borderWidth: 2.5,
        pointRadius: 5,
        pointHoverRadius: 7,
        showLine: true,
        tension: 0.2,
        order: 1,
      };

      return {
        datasets: [...refDatasets, patientDataset],
        meta: WHO[indicatorKey][sexKey],
        upperMonth: upper,
      };
    };
  }, [sexKey, edadAnios, patientPoints]);

  // Última medición de cada indicador (para tarjetas de resumen)
  const latestPercentiles = useMemo(() => {
    const out = {};
    INDICATORS.forEach((ind) => {
      const pts = patientPoints[ind.key] || [];
      out[ind.key] = pts.length ? pts[pts.length - 1] : null;
    });
    return out;
  }, [patientPoints]);

  return {
    paciente,
    consultas,
    loading,
    error,
    sexKey,
    edadAnios,
    isPediatric,
    indicators: INDICATORS,
    patientPoints,
    buildChartData,
    latestPercentiles,
  };
}

export default useWHOGrowthCharts;
